// AirPlay (Safari on iPhone, iPad and the Mac): WebKit sends the engine's own
// <audio> element to an Apple TV or AirPlay speaker. Nothing else changes: the
// engine keeps playing, seeking and syncing the element, and iOS shows the
// lock-screen Now Playing details (cover, title, author from Media Session) on
// the TV. Picking the iPhone in the picker brings the audio back here.

import { createStore } from '../lib/store';

export type AirPlayStatus =
  /** Not WebKit, or no AirPlay API. */
  | 'unsupported'
  /** AirPlay works here, but no device is on the network. */
  | 'unavailable'
  | 'available'
  /** The audio plays on an AirPlay device. */
  | 'connected';

export interface AirPlayState {
  status: AirPlayStatus;
}

export const airPlayStore = createStore<AirPlayState>({ status: 'unsupported' });

interface WebKitAudio extends HTMLAudioElement {
  webkitShowPlaybackTargetPicker?: () => void;
  webkitCurrentPlaybackTargetIsWireless?: boolean;
}

let element: WebKitAudio | null = null;

/** Safari exposes AirPlay on media elements through these WebKit APIs. */
export function airPlaySupported(win: Window = window): boolean {
  return typeof win !== 'undefined' && 'WebKitPlaybackTargetAvailabilityEvent' in win;
}

/** Turns AirPlay on for the engine's element and follows its availability. Idempotent. */
export function initAirPlay(audio: HTMLAudioElement): void {
  if (element === audio || !airPlaySupported()) return;
  const a = audio as WebKitAudio;
  if (typeof a.webkitShowPlaybackTargetPicker !== 'function') return;
  element = a;
  a.setAttribute('x-webkit-airplay', 'allow');
  let available = false;
  const show = () => {
    const status: AirPlayStatus = a.webkitCurrentPlaybackTargetIsWireless ? 'connected' : available ? 'available' : 'unavailable';
    if (airPlayStore.get().status !== status) airPlayStore.set({ status });
  };
  a.addEventListener('webkitplaybacktargetavailabilitychanged', (e) => {
    available = (e as Event & { availability?: string }).availability === 'available';
    show();
  });
  a.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', show);
  show();
}

/** Opens the system AirPlay picker (also how the listener moves the audio back). Call from a tap. */
export function showAirPlayPicker(): boolean {
  if (!element?.webkitShowPlaybackTargetPicker) return false;
  try {
    element.webkitShowPlaybackTargetPicker();
    return true;
  } catch (err) {
    console.warn('[airplay] picker failed', err);
    return false;
  }
}
